import React, { useCallback, useEffect, useState } from 'react';

import useScrollSpy from '../../hooks/useScrollSpy';
import useScrolled from '../../hooks/useScrolled';
import { MAILTO, NAV_LINKS } from '../../site';
import Logo from '../ui/Logo';
import './header.scss';

const SPY_IDS = NAV_LINKS.map((link) => link.id);

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const scrolled = useScrolled(8);
  const activeId = useScrollSpy(SPY_IDS);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  // Lock the page behind the mobile drawer and allow Esc to dismiss it.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeMenu();
    };

    document.body.classList.add('is-locked');
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.classList.remove('is-locked');
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen, closeMenu]);

  // A resize back to desktop should not leave the drawer state hanging around.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;

    const mql = window.matchMedia('(min-width: 921px)');
    const onChange = (event) => {
      if (event.matches) closeMenu();
    };

    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [closeMenu]);

  return (
    <header className={`site-header ${scrolled ? 'is-scrolled' : ''}`}>
      <div className="wrap site-header__bar">
        <a href="#top" className="site-header__brand" aria-label="BizVision Tech — back to top">
          <Logo variant="ink" />
        </a>

        <nav className="site-header__nav" aria-label="Main">
          <ul>
            {NAV_LINKS.map((link) => (
              <li key={link.id}>
                <a
                  href={`#${link.id}`}
                  className={activeId === link.id ? 'is-active' : ''}
                  aria-current={activeId === link.id ? 'true' : undefined}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="site-header__actions">
          <a href={MAILTO} className="btn btn--primary site-header__cta">
            Book a call
          </a>
          <button
            type="button"
            className={`site-header__burger ${menuOpen ? 'is-open' : ''}`}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span />
            <span />
          </button>
        </div>
      </div>

      <div
        id="mobile-menu"
        className={`mobile-menu ${menuOpen ? 'is-open' : ''}`}
        hidden={!menuOpen}
      >
        <nav aria-label="Mobile">
          <ul>
            {NAV_LINKS.map((link) => (
              <li key={link.id}>
                <a href={`#${link.id}`} onClick={closeMenu}>
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <a href={MAILTO} className="btn btn--primary" onClick={closeMenu}>
          Book a call
        </a>
      </div>

      <button
        type="button"
        className={`mobile-menu__scrim ${menuOpen ? 'is-open' : ''}`}
        tabIndex={-1}
        aria-hidden="true"
        onClick={closeMenu}
      />
    </header>
  );
}
